import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Camera, Loader2, Mic, Square, Volume2, X } from "lucide-react";

// Iter123k210 · Modalità Voice Loop continua ("parla-ascolta-parla").
// Flusso:
//  1) `recording` → raccoglie audio
//  2) stop su silenzio (10s senza voce) o pulsante stop
//  3) `transcribing` → POST /transcribe → ottieni testo
//  4) se testo contiene "chiudi" o "stop" → esce
//  5) altrimenti POST /chat → risposta Ginius
//  6) `speaking` → TTS → audio.play()
//  7) alla fine audio → torna al passo 1
//
// Modalità:
//  - "chat": loop chat con Ginius (hands-free durante sopralluogo)
//  - "intervista": loop guidato di 3-5 domande, risposte confluiscono in note_amministratore
//                 l'utente puo' confermare o scartare le risposte accumulate alla fine

const SILENCE_THRESHOLD = 10; // 10 secondi di silenzio = stop
const EXIT_WORDS_RE = /\b(chiudi|stop|basta|esci|fine)\b/i;
// Iter123k218 · comando vocale per scattare foto senza mani.
// Pattern frequenti dell'utente, trascrizione tollerante.
const CAMERA_WORDS_RE = /\b(scatta(?:la|lo|re)?|fai (?:una )?(?:foto|fotografia)|fotografa(?:la|lo)?|cattura|snap)\b/i;

const INTERVISTA_DOMANDE = [
  "Hai altre criticità da aggiungere al report?",
  "Vuoi suggerire una priorità di intervento (urgente, programmato, differibile)?",
  "C'è qualche aspetto da comunicare specificamente ai condomini?",
  "Vuoi aggiungere una stima di tempo o costo indicativo?",
];

export default function VoiceLoop({ sid, mode = "chat", onClose, onIntervistaResult, onCapturePhoto }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | transcribing | thinking | speaking
  const [heard, setHeard] = useState("");
  const [reply, setReply] = useState("");
  const [elapsed, setElapsed] = useState(0);
  // Iter123k216 · Scatta foto senza uscire dalla modalità vocale.
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  // Intervista state
  const [stepIdx, setStepIdx] = useState(0);
  const [respostes, setRespostes] = useState([]);  // {domanda, risposta}

  const mediaRecRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioElRef = useRef(null);
  const abortedRef = useRef(false);
  const timerRef = useRef(null);
  const silenceTimerRef = useRef(null);
  // Voice activity detection
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const vadIntervalRef = useRef(null);
  const lastVoiceAtRef = useRef(0);

  const stopAll = () => {
    abortedRef.current = true;
    try { mediaRecRef.current?.state !== "inactive" && mediaRecRef.current?.stop(); } catch { /* noop */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { audioElRef.current?.pause(); } catch { /* noop */ }
    try { audioCtxRef.current?.close(); } catch { /* noop */ }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (vadIntervalRef.current) { clearInterval(vadIntervalRef.current); vadIntervalRef.current = null; }
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    streamRef.current = null; mediaRecRef.current = null;
    audioCtxRef.current = null; analyserRef.current = null;
  };

  const sayAndContinue = async (text, cbAfterSpeak) => {
    if (abortedRef.current) return;
    setPhase("speaking");
    setReply(text);
    try {
      const r = await api.post("/copilot/tts", { testo: text }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "audio/mpeg" }));
      const audio = new Audio(url);
      audioElRef.current = audio;
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (abortedRef.current) return;
        if (cbAfterSpeak) cbAfterSpeak();
      };
      audio.onerror = () => { URL.revokeObjectURL(url); if (!abortedRef.current && cbAfterSpeak) cbAfterSpeak(); };
      await audio.play();
    } catch (e) {
      if (!abortedRef.current && cbAfterSpeak) cbAfterSpeak();
    }
  };

  const startRecording = async () => {
    if (abortedRef.current) return;
    setPhase("recording");
    setElapsed(0);
    audioChunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
                      : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data?.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (vadIntervalRef.current) { clearInterval(vadIntervalRef.current); vadIntervalRef.current = null; }
        try { audioCtxRef.current?.close(); } catch { /* noop */ }
        if (abortedRef.current) return;
        const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 1500) {
          // troppo corto, riprova
          setTimeout(() => { if (!abortedRef.current) startRecording(); }, 400);
          return;
        }
        await transcribe(blob, mr.mimeType);
      };
      mr.start();

      // VAD: attiva analyser per silence detection
      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = audioCtx;
        const src = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 2048;
        src.connect(analyser);
        analyserRef.current = analyser;
        const data = new Uint8Array(analyser.frequencyBinCount);
        lastVoiceAtRef.current = Date.now();
        vadIntervalRef.current = setInterval(() => {
          analyser.getByteTimeDomainData(data);
          // RMS
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length);
          if (rms > 0.03) lastVoiceAtRef.current = Date.now();
          const silenceFor = (Date.now() - lastVoiceAtRef.current) / 1000;
          if (silenceFor >= SILENCE_THRESHOLD) {
            // 10s di silenzio → stop
            try { mr.stop(); } catch { /* noop */ }
          }
        }, 300);
      } catch { /* VAD opzionale, se fallisce procedo lo stesso */ }

      // Timer visuale (max 60s)
      const startedAt = Date.now();
      timerRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startedAt) / 1000));
        if (Date.now() - startedAt > 60000) { try { mr.stop(); } catch { /* noop */ } }
      }, 500);
    } catch (e) {
      toast.error("Permesso microfono negato");
      stopAll();
      onClose?.();
    }
  };

  const transcribe = async (blob, mimeType) => {
    if (abortedRef.current) return;
    setPhase("transcribing");
    const ext = (mimeType || "").includes("mp4") ? "mp4" : (mimeType || "").includes("wav") ? "wav" : "webm";
    const fd = new FormData();
    fd.append("file", blob, `v.${ext}`);
    fd.append("save_as_note", "false");
    try {
      const r = await api.post(`/copilot/sessioni/${sid}/transcribe`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const testo = (r.data?.testo || "").trim();
      setHeard(testo);
      if (!testo) {
        // nessuna voce rilevata, riparti
        setTimeout(() => { if (!abortedRef.current) startRecording(); }, 400);
        return;
      }
      if (EXIT_WORDS_RE.test(testo)) {
        await sayAndContinue("Chiudo. A dopo!", () => {
          if (mode === "intervista") onIntervistaResult?.(respostes);
          stopAll(); onClose?.();
        });
        return;
      }
      // Iter123k218 · "scatta", "fai una foto", "cattura" → apri camera
      // (solo in modalità chat con callback foto disponibile).
      if (mode === "chat" && onCapturePhoto && CAMERA_WORDS_RE.test(testo)) {
        await sayAndContinue("Apro la fotocamera.", () => {
          // Delega al bottone esistente che a sua volta scatena il file input camera.
          try { fileInputRef.current?.click(); } catch { /* noop */ }
          // Riprendi l'ascolto dopo che l'utente ha scattato / annullato.
          setTimeout(() => { if (!abortedRef.current) startRecording(); }, 1500);
        });
        return;
      }
      // Normale: in chat → manda al CoPilot; in intervista → registra risposta e passa a prossima domanda
      if (mode === "intervista") {
        const q = INTERVISTA_DOMANDE[stepIdx];
        const nextList = [...respostes, { domanda: q, risposta: testo }];
        setRespostes(nextList);
        const next = stepIdx + 1;
        if (next < INTERVISTA_DOMANDE.length) {
          setStepIdx(next);
          await sayAndContinue(INTERVISTA_DOMANDE[next], () => startRecording());
        } else {
          await sayAndContinue(
            "Perfetto, ho tutto. Aggiungo le tue risposte alle note.",
            () => { onIntervistaResult?.(nextList); stopAll(); onClose?.(); },
          );
        }
        return;
      }
      // mode = chat → invia a Ginius
      setPhase("thinking");
      const cr = await api.post(`/copilot/sessioni/${sid}/chat`, { testo });
      const risposta = (cr.data?.agent_msg?.testo || "").trim();
      if (!risposta) {
        setTimeout(() => { if (!abortedRef.current) startRecording(); }, 400);
        return;
      }
      await sayAndContinue(risposta, () => startRecording());
    } catch (e) {
      // Iter123k215 · Su errori 4xx NON riprovare in loop: la causa è "stabile"
      // (sessione chiusa, 404, permessi) e un retry infinito frustra l'utente.
      const status = e?.response?.status;
      const detail = formatApiError(e.response?.data?.detail);
      if (status && status >= 400 && status < 500) {
        toast.error(detail || `Errore ${status}: interrompo l'ascolto`);
        stopAll();
        onClose?.();
        return;
      }
      toast.error(detail || "Elaborazione fallita, riprovo…");
      setTimeout(() => { if (!abortedRef.current) startRecording(); }, 1200);
    }
  };

  // Start al mount
  useEffect(() => {
    abortedRef.current = false;
    if (mode === "intervista") {
      sayAndContinue(INTERVISTA_DOMANDE[0], () => startRecording());
    } else {
      sayAndContinue("Sono qui, dimmi pure.", () => startRecording());
    }
    return () => { stopAll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const phaseLabel = {
    idle: "Pronto",
    recording: `Ascolto… ${elapsed}s`,
    transcribing: "Trascrivo…",
    thinking: "Ginius sta pensando…",
    speaking: "Ginius sta parlando…",
  }[phase] || "—";

  // Iter123k216 · Scatta foto senza chiudere la finestra vocale.
  // La fotocamera del telefono apre il viewfinder, allo scatto risaliamo al
  // callback del parent che fa upload + collegamento alla sessione.
  const scattaFoto = () => {
    if (!onCapturePhoto || uploadingPhoto) return;
    fileInputRef.current?.click();
  };
  // Iter123k220 · parole di consenso/dissenso per la domanda "la descrivo?"
  const YES_WORDS_RE = /\b(s[iì]|certo|ok|okay|va bene|sicuro|fai pure|descrivila|certamente|yes|yep)\b/i;

  const chiediEDescrivi = async (foto_id) => {
    // Chiedi all'utente se vuole la descrizione, poi ascolta UNA risposta corta.
    await sayAndContinue("Vuoi che la descriva?", async () => {
      if (abortedRef.current) return;
      // Mini-recording ad hoc (max 6 secondi, silenzio 3s) per catturare sì/no.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        // niente permessi: riprendi il loop normale senza descrivere
        setTimeout(() => { if (!abortedRef.current) startRecording(); }, 300);
        return;
      }
      const chunks = [];
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
                       : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4"
                       : "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mr.ondataavailable = (ev) => { if (ev.data?.size) chunks.push(ev.data); };
      mr.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
        const blob = new Blob(chunks, { type: mimeType || "audio/webm" });
        const fd = new FormData();
        fd.append("file", blob, "yesno.webm");
        fd.append("save_as_note", "false");
        let risposta = "";
        try {
          const r = await api.post(`/copilot/sessioni/${sid}/transcribe`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
          risposta = (r.data?.testo || "").trim();
        } catch { /* ignore */ }
        if (risposta && YES_WORDS_RE.test(risposta)) {
          setPhase("thinking");
          try {
            const v = await api.post(`/copilot/sessioni/${sid}/foto/${foto_id}/vision`);
            const did = (v.data?.didascalia || "").trim();
            if (did) {
              // Iter123k222 · auto-crea una nota collegata alla foto con la didascalia.
              // Totalmente hands-free: la nota appare nel tab Note con la foto associata.
              try {
                await api.post(`/copilot/sessioni/${sid}/note`,
                                 { testo: did, tipo: "manuale", foto_ids: [foto_id] });
              } catch { /* non bloccare la voce se la nota fallisce */ }
              await sayAndContinue(did, () => startRecording());
              return;
            }
          } catch (e) {
            const detail = formatApiError(e.response?.data?.detail);
            await sayAndContinue(detail || "Non riesco a descriverla ora.",
                                      () => startRecording());
            return;
          }
          await sayAndContinue("Didascalia non disponibile.", () => startRecording());
          return;
        }
        // nessuna conferma → procedi
        setTimeout(() => { if (!abortedRef.current) startRecording(); }, 300);
      };
      mr.start();
      // auto-stop a 4s
      setTimeout(() => { try { mr.stop(); } catch { /* noop */ } }, 4000);
    });
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !onCapturePhoto) return;
    setUploadingPhoto(true);
    let photo = null;
    try {
      photo = await onCapturePhoto(file);
    } catch (_err) { /* parent toasta già */ }
    setUploadingPhoto(false);
    if (photo?.id && mode === "chat") {
      await chiediEDescrivi(photo.id);
    } else {
      // nessun id: riprendi comunque l'ascolto se eravamo in listening
      if (!abortedRef.current && mode === "chat") {
        setTimeout(() => startRecording(), 300);
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-40 flex items-center justify-center p-4"
      data-testid="voice-loop">
      <Card className="w-full max-w-md p-5 bg-slate-900 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="font-bold text-lg flex items-center gap-2">
            <Mic className={`w-5 h-5 ${phase === "recording" ? "text-red-400 animate-pulse" : "text-emerald-400"}`} />
            {mode === "intervista" ? "Intervista di revisione" : "Modalità vocale continua"}
          </div>
          <button data-testid="voice-loop-close" onClick={() => { stopAll(); onClose?.(); }}
            className="p-1 hover:bg-white/10 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Big visualizer */}
        <div className="flex justify-center my-4">
          <div className={`w-28 h-28 rounded-full flex items-center justify-center border-4 ${
            phase === "recording" ? "border-red-500 animate-pulse bg-red-500/20"
            : phase === "speaking" ? "border-emerald-500 animate-pulse bg-emerald-500/20"
            : phase === "thinking" ? "border-amber-500 bg-amber-500/20"
            : phase === "transcribing" ? "border-blue-500 bg-blue-500/20"
            : "border-slate-500 bg-slate-800"
          }`} data-testid="voice-loop-indicator">
            {phase === "recording" && <Mic className="w-10 h-10 text-red-400" />}
            {phase === "transcribing" && <Loader2 className="w-10 h-10 animate-spin text-blue-300" />}
            {phase === "thinking" && <Loader2 className="w-10 h-10 animate-spin text-amber-300" />}
            {phase === "speaking" && <Volume2 className="w-10 h-10 text-emerald-400" />}
            {phase === "idle" && <Mic className="w-10 h-10 text-slate-400" />}
          </div>
        </div>

        <div className="text-center font-semibold mb-2" data-testid="voice-loop-phase">
          {phaseLabel}
        </div>

        {mode === "intervista" && (
          <div className="text-center text-xs text-slate-300 mb-2">
            Domanda {Math.min(stepIdx + 1, INTERVISTA_DOMANDE.length)} di {INTERVISTA_DOMANDE.length}
          </div>
        )}

        {heard && (
          <div className="bg-white/10 rounded px-2 py-1 text-xs mb-1">
            <span className="opacity-60">tu: </span>{heard}
          </div>
        )}
        {reply && (
          <div className="bg-emerald-900/60 rounded px-2 py-1 text-xs mb-2">
            <span className="opacity-60">Ginius: </span>{reply}
          </div>
        )}

        <div className="flex gap-2 mt-3">
          {phase === "recording" && (
            <Button data-testid="voice-loop-stop-record"
              onClick={() => { try { mediaRecRef.current?.stop(); } catch { /* noop */ } }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white">
              <Square className="w-4 h-4 mr-1" fill="currentColor" /> Fine turno
            </Button>
          )}
          {onCapturePhoto && mode === "chat" && (
            <Button data-testid="voice-loop-scatta-foto"
              onClick={scattaFoto}
              disabled={uploadingPhoto}
              title="Scatta foto senza uscire"
              className="flex-none bg-brand hover:bg-brand/90 text-white px-3">
              {uploadingPhoto
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Camera className="w-4 h-4" />}
            </Button>
          )}
          <Button data-testid="voice-loop-exit"
            onClick={() => {
              if (mode === "intervista" && respostes.length) onIntervistaResult?.(respostes);
              stopAll(); onClose?.();
            }}
            className="flex-1 bg-slate-700 hover:bg-slate-800 text-white">
            Esci
          </Button>
        </div>
        {/* input nascosto per acquisizione foto da camera (solo chat mode) */}
        {onCapturePhoto && mode === "chat" && (
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment"
            className="hidden" data-testid="voice-loop-file-input"
            onChange={onFileChosen} />
        )}
        <div className="text-[10px] text-slate-400 text-center mt-2">
          Dì "chiudi" o "stop" per uscire · auto-stop dopo {SILENCE_THRESHOLD}s di silenzio
          {onCapturePhoto && mode === "chat" && " · tocca la fotocamera per scattare"}
        </div>
      </Card>
    </div>
  );
}
